// Compara o estado do legado com a última base confirmada e produz UMA operação atômica.
// Saldos ficam fora da comparação de cadastro: só mudam por movimento no razão.
import { filament, stockItem, product, sale, production, purchase, movement, marketplace, settingsDoc, KIND_BY_KEY } from './mappers.js';

export const KEYS = {
  print3d_products: 'products', print3d_settings: 'settings', print3d_filamentos: 'filamentos', print3d_insumos: 'insumos',
  print3d_embalagens: 'embalagens', print3d_outros: 'outrosItens', print3d_mov: 'movimentacoes', print3d_vendas: 'vendas',
  print3d_producoes: 'producoes', print3d_compras: 'compras',
};
const stable = v => {
  if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
  if (v && typeof v === 'object') return '{' + Object.keys(v).sort().filter(k => v[k] !== undefined).map(k => JSON.stringify(k) + ':' + stable(v[k])).join(',') + '}';
  return JSON.stringify(v ?? null);
};

// Estado do legado -> { entidade: { id: { row, json } } }, saldos e ids de movimentos.
export function snapshot(state) {
  const nameOf = id => (state.products.find(p => p.id === id) || {}).nome || '';
  const rows = { filaments: {}, stock_items: {}, products: {}, sales: {}, productions: {}, purchases: {}, marketplace_settings: {}, system_settings: {} };
  const stock = {}; const owner = {};
  const put = (e, id, row) => {
    const { created_at, ...cmp } = row;
    rows[e][id] = { row, json: stable(cmp) };
  };
  for (const f of state.filamentos) { put('filaments', f.id, filament.toRow(f)); stock['filamento:' + f.id] = filament.stockOf(f); }
  for (const [key, kind] of Object.entries(KIND_BY_KEY)) for (const i of state[key]) { put('stock_items', i.id, stockItem.toRow(i, kind)); stock[kind + ':' + i.id] = stockItem.stockOf(i); owner[kind + ':' + i.id] = i.nome; }
  for (const p of state.products) {
    put('products', p.id, product.toRow(p));
    for (const [vid, q] of Object.entries(product.stocksOf(p))) { stock['produto:' + p.id + ':' + vid] = q; owner['produto:' + p.id + ':' + vid] = p.nome; }
  }
  for (const v of state.vendas) put('sales', v.id, sale.toRow(v, nameOf));
  for (const pr of state.producoes) put('productions', pr.id, production.toRow(pr, nameOf));
  for (const c of state.compras) put('purchases', c.id, purchase.toRow(c));
  for (const [ch, p] of Object.entries(state.settings.platforms || {})) put('marketplace_settings', ch, marketplace.toRow(ch, p));
  put('system_settings', 'settings', settingsDoc.toRow(state.settings));
  return { rows, stock, owner, movIds: new Set(state.movimentacoes.map(m => m.id)) };
}

const keyOfMov = r => r.item_kind === 'produto' ? `produto:${r.item_id}:${r.variation_id}` : `${r.item_kind}:${r.item_id}`;

export function diff(base, cur, state, uuid) {
  const ops = [];
  for (const entity of Object.keys(cur.rows)) {
    const a = base.rows[entity] || {}, b = cur.rows[entity];
    for (const [id, c] of Object.entries(b)) {
      if (!a[id]) ops.push({ entity, id, action: 'upsert', data: c.row, isNew: entity !== 'system_settings' && entity !== 'marketplace_settings' });
      else if (a[id].json !== c.json) ops.push({ entity, id, action: 'upsert', data: c.row });
    }
    for (const id of Object.keys(a)) if (!b[id]) ops.push({ entity, id, action: 'delete' });
  }
  // movimentacoes é mantida com o mais recente primeiro; o servidor precisa da ordem cronológica.
  const fresh = state.movimentacoes.filter(m => !base.movIds.has(m.id)).reverse().map(m => movement.toRow(m));
  const explained = {};
  for (const m of fresh) {
    if (m.item_kind === 'produto' && !m.variation_id) m.variation_id = firstVar(state, m.item_id);
    const k = keyOfMov(m); explained[k] = (explained[k] || 0) + m.qty_in - m.qty_out;
  }
  // Mudança de saldo sem movimento (ex.: estoque inicial no cadastro) vira ajuste explícito: o razão sempre fecha.
  for (const k of new Set([...Object.keys(cur.stock), ...Object.keys(base.stock)])) {
    if (!(k in cur.stock)) continue;
    const delta = round(cur.stock[k] - (base.stock[k] || 0)) - round(explained[k] || 0);
    if (Math.abs(delta) < 1e-6) continue;
    const [kind, id, vid] = k.split(':');
    fresh.push({ id: uuid(), occurred_at: new Date().toISOString(), item_kind: kind, item_id: id, variation_id: vid || null,
      item_name: cur.owner[k] || '', mov_type: base.stock[k] === undefined ? 'entrada' : 'ajuste', qty_in: delta > 0 ? round(delta) : 0, qty_out: delta < 0 ? round(-delta) : 0,
      reason: base.stock[k] === undefined ? 'Estoque inicial' : 'Ajuste de saldo', reference: '', notes: '' });
  }
  return { ops, movements: fresh };
}
const round = v => Math.round(v * 1e4) / 1e4;
const firstVar = (state, pid) => (state.products.find(p => p.id === pid)?.variacoes?.[0] || {}).id || null;
